import type { CardContentTypeT, CardTypeT, SpecialRowContainerBackgroundT } from "@setplex/arcana"

import type { AppearanceRow } from '@setplex/tria-api'
import type { ViewComponent, ViewRow } from '../../model/components/index.h'

import { CardType } from "/app/libraries/arcana/src/blocks/Cards/index.ts"
import { FIXED_INDENTS } from "/app/libraries/arcana/src/vars/index.ts"
import { LabelFonts } from "/app/libraries/arcana/src/atoms/Label/index.ts"
import { TextBoxTypes } from "/app/libraries/arcana/src/atoms/TextBox/index.ts"


export const getCategoryId = (params: URLSearchParams): number => {
  const categoryId = params.get('categoryId') ?? 0

  return Number(categoryId)
}

export const getCollectionId = (params: URLSearchParams): string => {
  const collectionId = params.get('collectionId') ?? ''

  return String(collectionId)
}

export const getOnlyFavorites = (params: URLSearchParams): boolean =>
  params.get('only-favorites') === 'true'

export const getRowBackgroundStyle = (row?: ViewComponent<AppearanceRow>) => {
  const background = row?.appearance.style.background

  const rowBackground: Record<string, SpecialRowContainerBackgroundT> = {
    GRADIENT: 'SimpleGradient',
    IMAGE: 'SimpleFullWitdhImage',
    COLOR: 'none',
    UNSET: 'none',
  }

  return background?.type ? rowBackground[background.type] : 'none'
}

export const getRowBackgroundGradient = (
  row?: ViewComponent<AppearanceRow>
) => {
  const background = row?.appearance.style.background

  if (background?.type === 'GRADIENT') {
    const config = background.configuration
    const colors = config.list.map(
      (colorConfig) => `${colorConfig.color} ${colorConfig.percentage}%`
    )
    return `linear-gradient(${config.angle}deg, ${colors.join(', ')})`
  }
}

export const getRowPosterImage = (row?: ViewComponent<AppearanceRow>) => {
  const background = row?.appearance.style.background
  if (background?.type === 'IMAGE') return background.imageUrl
}

export const getRowContentType = (row?: ViewRow): CardContentTypeT => {
  switch (row?.appearance.contentType) {
    case 'VOD':
    case 'MOTN_VOD':
    case 'TV_SHOW':
    case 'MOTN_TV_SHOW':
    case 'MIXED':
    case 'PARTNER_PRODUCT':
    case 'VOD_BUNDLE':
    case 'TV_BUNDLE':
    case 'TV_SHOW_BUNDLE':
      return 'VODS_ADAPTIVE'
    case 'TV_CHANNEL':
      return 'CHANNELS_ADAPTIVE'
    case 'LIVE_EVENT':
      return row.appearance.style?.topContent
        ? 'EVENTS_ADAPTIVE'
        : 'VODS_ADAPTIVE'
    default:
      return 'VODS_ADAPTIVE'
  }
}

export const getCardFonts = (size: 'S' | 'M' | 'L') => {
  const fontTitleConfig = {
    S: TextBoxTypes.Footnote5,
    M: TextBoxTypes.FontSmBold,
    L: TextBoxTypes.Footnote5,
  }

  const fontLabelConfig = {
    S: LabelFonts.Body4,
    M: LabelFonts.Body4,
    L: LabelFonts.Body4,
  }

  return {
    titleFont: fontTitleConfig[size],
    labelFont: fontLabelConfig[size],
  }
}

export const getCardTitleMaxLines = (orientation: 'HORIZONTAL' | 'VERTICAL') =>
  orientation === 'VERTICAL' ? 2 : 1

export const getCardContentPadding = (size: 'S' | 'M' | 'L') => {
  const contentPaddingConfig = {
    S: FIXED_INDENTS.INDENT_40,
    M: FIXED_INDENTS.INDENT_50,
    L: FIXED_INDENTS.INDENT_60,
  }

  return contentPaddingConfig[size]
}

export const getCWCardPadding = (size: 'S' | 'M' | 'L') => {
  const paddingConfig = {
    S: FIXED_INDENTS.INDENT_20,
    M: FIXED_INDENTS.INDENT_25,
    L: FIXED_INDENTS.INDENT_30,
  }

  return paddingConfig[size]
}

export const getBundleCardConfig = (size: 'S' | 'M' | 'L') => {
  const fontTitleConfig = {
    S: TextBoxTypes.Footnote4,
    M: TextBoxTypes.Footnote3,
    L: TextBoxTypes.Footnote2,
  }

  const fontSubTitleConfig = {
    S: TextBoxTypes.Caption4,
    M: TextBoxTypes.Caption3,
    L: TextBoxTypes.Caption2,
  }

  const verticalGapConfig = {
    S: FIXED_INDENTS.INDENT_0,
    M: FIXED_INDENTS.INDENT_10,
    L: FIXED_INDENTS.INDENT_10,
  }

  return {
    titleFont: fontTitleConfig[size],
    subTitleFont: fontSubTitleConfig[size],
    verticalGap: verticalGapConfig[size],
  }
}

export const getDataTestIdRow = (row: ViewComponent<AppearanceRow>) => {
  return `${row.appearance.localizedTitleKey}_row`
}

export const getRowType = (row: ViewComponent<AppearanceRow>): CardTypeT => {
  const { contentType, style } = row.appearance

  if (style?.topContent) {
    if (contentType === 'TV_CHANNEL') {
      return CardType.HORIZONTAL_CHANNEL_TOP_CONTENT
    }

    if (contentType === 'LIVE_EVENT') {
      if (style.posterOrientation === 'VERTICAL') {
        return CardType.VERTICAL_EVENT_TOP_CONTENT
      }

      return CardType.HORIZONTAL_EVENT_TOP_CONTENT
    }

    if (style.posterOrientation === 'VERTICAL') {
      return CardType.VERTICAL_VOD_TOP_CONTENT
    }

    return CardType.HORIZONTAL_VOD_TOP_CONTENT
  }

  // TV channel rows only have horizontal visible-card configs in Arcana.
  // Old/incomplete payloads can fallback to `VERTICAL`, which later crashes
  // in `useSimpleVisibleCards` on `RowVisibleCards.CHANNELS_ADAPTIVE.VERTICAL`.
  if (contentType === 'TV_CHANNEL') {
    return CardType.HORIZONTAL
  }

  return row.appearance.style.posterOrientation
}
